// Resume41.js - Fixed Full Version + Pagination (like Resume1)
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  LinkedIn as LinkedInIcon,
  Language as LanguageIcon,
  DriveEta as DriveEtaIcon,
} from "@mui/icons-material";

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

// Pixel heights for pagination calculations
const pageSizePxMap = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const defaultData = {
  firstName: "Ndememi",
  lastName: "Otieneno",
  currentPosition: "IT Project Manager",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "123 Anywhere St., Any City",
  website: "www.reallygreatsite.com",
  linkedIn: "",
  drivingLicence: "",
  profile: "",
};

const defaultLeftSectionOrder = ["contact", "skills"];

const defaultRightSectionOrder = [
  "profile",
  "education",
  "workExperience",
  "languages",
  "accomplishments",
  "certifications",
  "softwareSkills",
  "volunteering",
  "interests",
  "websites",
  "references",
  "additionalInfo",
];

const DEFAULT_FORM_DATA = {};
const DEFAULT_WORK_EXPERIENCES = [];

const Resume41 = ({
  color = "#5d4037",
  font = "Arial, Helvetica, sans-serif",
  fontSize = "14px",
  headingSize = "24px",
  fontStyle = "normal",
  sectionSpacing = 50,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = DEFAULT_FORM_DATA,
  photo = "",
  workExperiences = DEFAULT_WORK_EXPERIENCES,
  exporting = false,
  enableDragDrop = true,
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const pagePx = pageSizePxMap[pageSize] || pageSizePxMap.A4;
  const data = { ...defaultData, ...formData };

  const safeJSON = (str, fallback = null) => {
    try {
      return str ? JSON.parse(str) : fallback;
    } catch {
      return fallback;
    }
  };

  const ensureArray = (val) => (Array.isArray(val) ? val : []);

  // ── Pagination states (same as Resume1) ─────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageBreaks, setPageBreaks] = useState([]);
  const rightContentRef = useRef(null);

  // All data states
  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  // Section ordering
  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  const sectionSpacingValue = sectionSpacing / 10;

  // Resolve font sizes (support 'small' | 'medium' | 'large' aliases)
  const sizeMap = {
    small: { body: '12px', heading: '18px' },
    medium: { body: '14px', heading: '22px' },
    large: { body: '16px', heading: '26px' },
  };
  const bodyFontSize = (['small', 'medium', 'large'].includes(fontSize) ? sizeMap[fontSize].body : fontSize) || '14px';
  const headingFontSize = (['small', 'medium', 'large'].includes(headingSize) ? sizeMap[headingSize].heading : headingSize) || '24px';
  const paragraphSpacingPx = `${paragraphSpacing}px`;
  const toPx = (v) => {
    if (!v) return 14;
    if (typeof v === 'number') return v;
    if (v.endsWith('px')) return parseFloat(v);
    if (v.endsWith('rem')) return parseFloat(v) * 16;
    return parseFloat(v) || 14;
  };
  const strongFontSize = `${Math.round(toPx(bodyFontSize) * 1.05)}px`;
  
  // Paragraph spacing helper: compute spacing for different contexts
  const itemSpacing = `${Math.round(paragraphSpacing / 3.5)}px`;  // ~8.5px when default 30px
  const paragraphSpacingLarge = `${Math.round(paragraphSpacing / 1.5)}px`;  // ~20px when default 30px
  const listItemSpacing = `${Math.round(paragraphSpacing / 4)}px`;  // ~7.5px when default 30px

  // ─── Load from localStorage ────────────────────────────────────────────────
  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(ensureArray(safeJSON(storedEntries, [])));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(ensureArray(safeJSON(storedSkills, [])));

    const storedSummaries = ensureArray(safeJSON(localStorage.getItem("summaries"), []));
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    const storedAdditionalInfo = localStorage.getItem("additionalInfo");
    if (storedAdditionalInfo) {
      const parsed = safeJSON(storedAdditionalInfo, []);
      setSavedAdditionalInfo(Array.isArray(parsed) ? parsed : [parsed]);
    }

    const storedLanguages = ensureArray(safeJSON(localStorage.getItem("languagesList"), []));
    setSavedLanguages(storedLanguages);

    const storedAccomplishments = ensureArray(safeJSON(localStorage.getItem("accomplishmentsList"), []));
    setSavedAccomplishments(storedAccomplishments);

    const storedCertifications = ensureArray(safeJSON(localStorage.getItem("certificationsList"), []));
    setSavedCertifications(storedCertifications);

    const storedReferences = ensureArray(safeJSON(localStorage.getItem("referencesList"), []));
    setSavedReferences(storedReferences);

    const storedSoftwareSkills = ensureArray(safeJSON(localStorage.getItem("softwareSkills"), []));
    setSoftwareSkills(storedSoftwareSkills);

    const storedVolunteering = ensureArray(safeJSON(localStorage.getItem("volunteering"), []));
    setSavedVolunteering(storedVolunteering);

    const storedInterests = ensureArray(safeJSON(localStorage.getItem("interests"), []));
    setSavedInterests(storedInterests);

    const storedWebsites = ensureArray(safeJSON(localStorage.getItem("websites"), []));
    setSavedWebsites(storedWebsites);

    // Custom sections
    const userId = localStorage.getItem("userId");
    let sectionsData = {};
    if (userId) {
      const currentCustomSections = ensureArray(safeJSON(localStorage.getItem(`current_custom_sections_${userId}`), []));
      currentCustomSections.forEach((sectionName) => {
        const userCustomKey = `custom_${userId}_${sectionName}`;
        try {
          const customSectionData = localStorage.getItem(userCustomKey);
          if (customSectionData) {
            const parsedData = safeJSON(customSectionData, {});
            const items = ensureArray((parsedData && parsedData.items) || []).filter((item) => item && item.trim() !== "");
            if (items.length > 0) sectionsData[sectionName] = items;
          }
        } catch (e) {
          console.error("Error loading custom section:", e);
        }
      });
    }
    setCustomSections(sectionsData);

    // Section orders
    const savedLeft = localStorage.getItem("resume41LeftSectionOrder");
    if (savedLeft) {
      setLeftSectionOrder(ensureArray(safeJSON(savedLeft, [])).filter((s) => s && typeof s === "string" && !s.startsWith("custom_")));
    }

    const savedRight = localStorage.getItem("resume41RightSectionOrder");
    const customIds = Object.keys(sectionsData).map((n) => `custom_${n}`);
    if (savedRight) {
      const parsed = ensureArray(safeJSON(savedRight, [])).filter((s) => s && typeof s === "string" && !s.startsWith("custom_"));
      setRightSectionOrder([...parsed, ...customIds]);
    } else {
      setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
    }
  }, []);

  // Persist section orders
  useEffect(() => {
    if (leftSectionOrder.length > 0)
      localStorage.setItem("resume41LeftSectionOrder", JSON.stringify(leftSectionOrder));
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0)
      localStorage.setItem("resume41RightSectionOrder", JSON.stringify(rightSectionOrder));
  }, [rightSectionOrder]);

  // Sync custom sections into right order
  useEffect(() => {
    const customIds = Object.keys(customSections).map((n) => `custom_${n}`);
    setRightSectionOrder((prev) => {
      const base = prev.filter((s) => !s.startsWith("custom_"));
      return [...base, ...customIds];
    });
  }, [customSections]);

  // ── Pagination calculation (same logic as Resume1) ──────────────────────
  useEffect(() => {
    if (exporting) return;
    const container = rightContentRef.current;
    if (!container) return;

    const pageHeight = pagePx.height - (topBottomMargin + 20) * 2;
    const sections = container.querySelectorAll(".resume41-section");
    const breaks = [];
    let accumulated = 0;
    let pageIndex = 1;

    sections.forEach((section) => {
      const h = section.getBoundingClientRect().height;
      if (accumulated + h > pageHeight * pageIndex) {
        breaks.push(pageIndex);
        pageIndex++;
      }
      accumulated += h;
    });

    const total = Math.max(1, pageIndex);
    setTotalPages(total);
    setPageBreaks(breaks);
  }, [
    rightSectionOrder, savedSummary, workExperiences, educationEntries,
    savedSkills, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, savedAdditionalInfo, customSections,
    topBottomMargin, pagePx.height, exporting,
    lineSpacing, fontSize, headingSize, font, paragraphSpacing, sectionSpacing, sideMargins
  ]);

  const isRightSectionActive = (sectionId) => {
    if (sectionId === "profile") return !!(savedSummary && typeof savedSummary === "string");
    if (sectionId === "education") return getEducationData().length > 0;
    if (sectionId === "workExperience") return workExperiences.length > 0;
    if (sectionId === "languages") return savedLanguages.length > 0;
    if (sectionId === "accomplishments") return savedAccomplishments.length > 0;
    if (sectionId === "certifications") return savedCertifications.length > 0;
    if (sectionId === "softwareSkills") return softwareSkills.length > 0;
    if (sectionId === "volunteering") return savedVolunteering.length > 0;
    if (sectionId === "interests") return savedInterests.length > 0;
    if (sectionId === "websites") return savedWebsites.length > 0;
    if (sectionId === "references") return savedReferences.length > 0;
    if (sectionId === "additionalInfo") return savedAdditionalInfo.length > 0;
    if (sectionId.startsWith("custom_")) {
      const name = sectionId.replace("custom_", "");
      return !!(customSections[name] && customSections[name].length > 0);
    }
    return false;
  };

  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    const overflowStyles = "word-break: break-word; overflow-wrap: break-word; white-space: normal;";
    return htmlContent.replace(/<(div|p|span|li)([^>]*)/g, (match, tagName, attrs) => {
      const styleRegex = /style="([^"]*)"/;
      const styleMatch = attrs.match(styleRegex);
      const defaultStyles = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; ${overflowStyles}`;
      if (styleMatch) {
        const existingStyle = styleMatch[1];
        const needsFontSize = !existingStyle.includes('font-size:');
        const fontSizeStyle = needsFontSize ? `font-size: ${bodyFontSize};` : '';
        const mergedStyle = `style="${defaultStyles} ${fontSizeStyle} ${existingStyle}"`;
        return `<${tagName}${attrs.replace(styleRegex, mergedStyle)}`;
      } else {
        return `<${tagName}${attrs} style="${defaultStyles} font-size: ${bodyFontSize};"`;
      }
    });
  };

  const formatDate = (month, year) => {
    if (!month && !year) return "";
    if (!month) return year;
    if (!year) return month;
    return `${month} ${year}`;
  };

  // ─── Education helper ──────────────────────────────────────────────────────
  const getEducationData = () => {
    try {
      const forResume = localStorage.getItem("educationEntriesForResume");
      if (forResume) {
        const parsed = safeJSON(forResume, []);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      }

      const storedEntries = localStorage.getItem("educationEntries");
      if (!storedEntries) return [];

      const parsed = safeJSON(storedEntries, []);
      if (!Array.isArray(parsed)) return [];

      return parsed.filter(Boolean).map((entry) => {
        if (!entry) return null;
        if (entry.educationLevel === "school") {
          return {
            id: entry.id,
            degree: `${entry.standard || ""}${entry.standard ? " Standard" : ""}`,
            fieldOfStudy: entry.stream || "General Education",
            schoolName: entry.schoolName || "",
            schoolLocation: entry.schoolLocation || "",
            gradMonth: "",
            gradYear: "",
            additionalCoursework: [
              entry.district,
              entry.percentage && `Percentage: ${entry.percentage}%`,
            ]
              .filter(Boolean)
              .join(" | "),
          };
        }
        return {
          id: entry.id,
          degree: `${entry.degreeName || ""}${entry.programType ? ` (${entry.programType})` : ""}`,
          fieldOfStudy: entry.specialization || entry.degreeType || "",
          schoolName: entry.collegeName || "",
          schoolLocation: entry.collegeLocation || "",
          gradMonth: entry.gradMonth || "",
          gradYear: entry.gradYear || "",
          additionalCoursework: [
            entry.cgpa && `CGPA: ${entry.cgpa}`,
            entry.percentage && `Percentage: ${entry.percentage}%`,
            entry.grade && `Grade: ${entry.grade}`,
            entry.additionalCoursework,
          ]
            .filter(Boolean)
            .join(" | "),
        };
      }).filter(Boolean);
    } catch (e) {
      console.error("Error in getEducationData:", e);
      return [];
    }
  };

  // ─── Drag & Drop handlers ──────────────────────────────────────────────────
  const handleDragStart = (e, index, side) => {
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        index,
        side,
        sectionId: side === "left" ? leftSectionOrder[index] : rightSectionOrder[index],
      })
    );
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleDrop = (e, targetIndex, targetSide) => {
    e.preventDefault();
    const { index: sourceIndex, side: sourceSide, sectionId } = JSON.parse(
      e.dataTransfer.getData("text/plain")
    );

    if (sourceSide === targetSide) {
      const list = sourceSide === "left" ? [...leftSectionOrder] : [...rightSectionOrder];
      const [moved] = list.splice(sourceIndex, 1);
      list.splice(targetIndex, 0, moved);
      sourceSide === "left" ? setLeftSectionOrder(list) : setRightSectionOrder(list);
    } else {
      const srcList = sourceSide === "left" ? [...leftSectionOrder] : [...rightSectionOrder];
      const tgtList = targetSide === "left" ? [...leftSectionOrder] : [...rightSectionOrder];
      srcList.splice(sourceIndex, 1);
      tgtList.splice(targetIndex, 0, sectionId);
      if (sourceSide === "left") {
        setLeftSectionOrder(srcList);
        setRightSectionOrder(tgtList);
      } else {
        setRightSectionOrder(srcList);
        setLeftSectionOrder(tgtList);
      }
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = "1";
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
  };

  // ─── Section title map ─────────────────────────────────────────────────────
  const getSectionTitle = (sectionId) => {
    const titles = {
      contact: "Contact",
      education: "Education",
      skills: "Skills",

      profile: "Summary",
      workExperience: "Work Experience",
      languages: "Languages",
      accomplishments: "Accomplishments",
      certifications: "Certifications",
      softwareSkills: "Software Skills",
      volunteering: "Volunteering",
      interests: "Interests",
      websites: "Websites/Profiles",
      references: "References",
      additionalInfo: "Additional Information",
    };
    if (sectionId.startsWith("custom_")) return sectionId.replace("custom_", "").toUpperCase();
    return titles[sectionId] || sectionId;
  };

  // ─── Shared style helpers ──────────────────────────────────────────────────
  const leftHeadingStyle = {
    fontSize: headingFontSize,
    fontWeight: "bold",
    marginBottom: "16px",
    marginTop: "0",
    color: color,
    textTransform: "uppercase",
    letterSpacing: "1.4px",
    fontFamily: font,
  };

  const leftListStyle = {
    listStyle: "none",
    padding: 0,
    margin: 0,
    fontSize: bodyFontSize,
    lineHeight: `${lineSpacing}px`,
    color: "#444",
    fontFamily: font,
  };

  const rightHeadingStyle = {
    fontSize: headingFontSize,
    fontWeight: "bold",
    marginBottom: "18px",
    marginTop: "0",
    color: color,
    textTransform: "uppercase",
    letterSpacing: "1.3px",
    fontFamily: font,
    borderBottom: `${lineWeight}px solid #e0d0bf`,
    paddingBottom: "6px",
  };

  const bulletStyle = { color: color, fontSize: bodyFontSize, flexShrink: 0 };
  const circleStyle = { fontSize: strongFontSize, color: "#8d6e63", lineHeight: "1", flexShrink: 0 };

  // ─── Left column renderer ──────────────────────────────────────────────────
  const renderLeftSection = (sectionId) => {
    switch (sectionId) {
      case "contact":
        return (
          <div key="contact">
            <h3 style={leftHeadingStyle}>CONTACT</h3>
            <ul style={leftListStyle}>
              {data.email && (
                <li style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing }}>
                  <EmailIcon sx={{ fontSize: toPx(bodyFontSize), color: "#5d4037", mt: "2px", flexShrink: 0 }} />
                  <span style={{ wordBreak: "break-all" }}>{data.email}</span>
                </li>
              )}
              {data.phone && (
                <li style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing }}>
                  <PhoneIcon sx={{ fontSize: toPx(bodyFontSize), color: "#5d4037", mt: "2px", flexShrink: 0 }} />
                  <span>{data.phone}</span>
                </li>
              )}
              {data.city && (
                <li style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing }}>
                  <LocationOnIcon sx={{ fontSize: toPx(bodyFontSize), color: "#5d4037", mt: "2px", flexShrink: 0 }} />
                  <span>{data.city}</span>
                </li>
              )}
              {data.website && (
                <li style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing }}>
                  <LanguageIcon sx={{ fontSize: toPx(bodyFontSize), color: "#1976d2", mt: "2px", flexShrink: 0 }} />
                  <a
                    href={data.website.startsWith("http") ? data.website : `https://${data.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1976d2", textDecoration: "none", wordBreak: "break-all", fontSize: bodyFontSize }}
                  >
                    {data.website.replace(/^https?:\/\/(www\.)?/, "")}
                  </a>
                </li>
              )}
              {data.linkedIn && (
                <li style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing }}>
                  <LinkedInIcon sx={{ fontSize: toPx(bodyFontSize), color: "#0077b5", mt: "2px", flexShrink: 0 }} />
                  <a
                    href={data.linkedIn.startsWith("http") ? data.linkedIn : `https://${data.linkedIn}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#0077b5", textDecoration: "none", wordBreak: "break-all", fontSize: bodyFontSize }}
                  >
                    {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, "")}
                  </a>
                </li>
              )}
              {data.drivingLicence && (
                <li style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing }}>
                  <DriveEtaIcon sx={{ fontSize: toPx(bodyFontSize), color: "#4caf50", mt: "2px", flexShrink: 0 }} />
                  <span>DL: {data.drivingLicence}</span>
                </li>
              )}
            </ul>
          </div>
        );

      case "skills":
        const activeSkills = ensureArray(savedSkills).filter(Boolean);
        return activeSkills.length > 0 ? (
          <div key="skills">
            <h3 style={leftHeadingStyle}>SKILLS</h3>
            <ul style={leftListStyle}>
              {activeSkills.map((skill, i) => (
                <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: listItemSpacing }}>
                  <span style={bulletStyle}>•</span>
                  <span>
                    {typeof skill === "object" && skill !== null
                      ? (skill.name || skill.skillName || skill.skill || "")
                      : (skill || "")}
                    {skill && typeof skill === "object" && skill !== null && skill.rating ? ` (${skill.rating}%)` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null;


      case "languages":
        const activeLanguages = ensureArray(savedLanguages).filter(Boolean);
        return activeLanguages.length > 0 ? (
          <div key="languages">
            <h3 style={leftHeadingStyle}>LANGUAGES</h3>
            <ul style={leftListStyle}>
              {activeLanguages.map((lang, i) => {
                const name = typeof lang === "object" && lang !== null ? (lang.name || lang.language || "") : (lang || "");
                const level = typeof lang === "object" && lang !== null ? (lang.level || "") : "";
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: listItemSpacing }}>
                    <span style={bulletStyle}>•</span>
                    <span>{name}{level ? ` (${level})` : ""}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "certifications":
        const activeCerts = ensureArray(savedCertifications).filter(Boolean);
        return activeCerts.length > 0 ? (
          <div key="certifications">
            <h3 style={leftHeadingStyle}>CERTIFICATIONS</h3>
            <ul style={leftListStyle}>
              {activeCerts.map((cert, i) => {
                const name = typeof cert === "object" && cert !== null ? (cert.name || cert.certName || cert.title || "") : (cert || "");
                const provider = typeof cert === "object" && cert !== null ? (cert.provider || "") : "";
                const year = typeof cert === "object" && cert !== null ? (cert.year || "") : "";
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: listItemSpacing }}>
                    <span style={bulletStyle}>•</span>
                    <span>{name}{provider ? ` – ${provider}` : ""}{year ? ` (${year})` : ""}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "softwareSkills":
        const activeSoft = ensureArray(softwareSkills).filter(Boolean);
        return activeSoft.length > 0 ? (
          <div key="softwareSkills">
            <h3 style={leftHeadingStyle}>SOFTWARE SKILLS</h3>
            <ul style={leftListStyle}>
              {activeSoft.map((skill, i) => {
                const name = typeof skill === "object" && skill !== null ? (skill.name || skill.software || "") : (skill || "");
                const level = typeof skill === "object" && skill !== null ? (skill.level || skill.rating || "") : "";
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px" }}>
                    <span style={bulletStyle}>•</span>
                    <span>{name}{level ? ` — ${level}%` : ""}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "interests":
        const activeInterests = ensureArray(savedInterests).filter(Boolean);
        return activeInterests.length > 0 ? (
          <div key="interests">
            <h3 style={leftHeadingStyle}>INTERESTS</h3>
            <ul style={leftListStyle}>
              {activeInterests.map((interest, i) => {
                const text = typeof interest === "object" && interest !== null ? (interest.name || interest.interest || "") : (interest || "");
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: listItemSpacing }}>
                    <span style={bulletStyle}>•</span>
                    <span>{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "websites":
        const activeWebsites = ensureArray(savedWebsites).filter(Boolean);
        return activeWebsites.length > 0 ? (
          <div key="websites">
            <h3 style={leftHeadingStyle}>WEBSITES / PROFILES</h3>
            <ul style={leftListStyle}>
              {activeWebsites.map((site, i) => {
                const url = typeof site === "object" && site !== null ? (site.url || site.link || "") : (site || "");
                const name = typeof site === "object" && site !== null ? (site.name || site.label || url) : url;
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: listItemSpacing }}>
                    <span style={bulletStyle}>•</span>
                    <a
                      href={url && url.startsWith("http") ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1976d2", textDecoration: "none", wordBreak: "break-all" }}
                    >
                      {name || url}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "accomplishments":
        const activeAcc = ensureArray(savedAccomplishments).filter(Boolean);
        return activeAcc.length > 0 ? (
          <div key="accomplishments">
            <h3 style={leftHeadingStyle}>ACCOMPLISHMENTS</h3>
            <ul style={leftListStyle}>
              {activeAcc.map((acc, i) => {
                const text = typeof acc === "object" && acc !== null ? (acc.title || acc.description || acc.name || "") : (acc || "");
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: listItemSpacing }}>
                    <span style={bulletStyle}>•</span>
                    <span>{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "references":
        const activeRefs = ensureArray(savedReferences).filter(Boolean);
        return activeRefs.length > 0 ? (
          <div key="references">
            <h3 style={leftHeadingStyle}>REFERENCES</h3>
            <ul style={leftListStyle}>
              {activeRefs.map((ref, i) => {
                const text = typeof ref === "object" && ref !== null ? `${ref.refName || ref.name || ""}${ref.company ? ` - ${ref.company}` : ""}` : (ref || "");
                return (
                  <li key={i} style={{ marginBottom: itemSpacing }}>
                    <span style={{ whiteSpace: "pre-line" }}>{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "additionalInfo":
        const activeInfo = ensureArray(savedAdditionalInfo).filter(Boolean);
        return activeInfo.length > 0 ? (
          <div key="additionalInfo">
            <h3 style={leftHeadingStyle}>ADDITIONAL INFORMATION</h3>
            <ul style={leftListStyle}>
              {activeInfo.map((info, i) => {
                const text = typeof info === "object" && info !== null ? (info.info || info.text || info.description || "") : (info || "");
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: listItemSpacing }}>
                    <span style={bulletStyle}>•</span>
                    <span>{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      default:
        if (sectionId.startsWith("custom_")) {
          const name = sectionId.replace("custom_", "");
          const items = ensureArray(customSections[name]).filter(Boolean);
          return items.length > 0 ? (
            <div key={sectionId}>
              <h3 style={leftHeadingStyle}>{name.toUpperCase()}</h3>
              <ul style={leftListStyle}>
                {items.map((item, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: listItemSpacing }}>
                    <span style={bulletStyle}>•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null;
        }
        return null;
    }
  };

  // ─── Right column renderer ─────────────────────────────────────────────────
  const renderRightSection = (sectionId) => {
    const educationData = getEducationData();

    switch (sectionId) {
      case "profile":
        return savedSummary && typeof savedSummary === "string" ? (
          <div key="profile" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <p style={{ fontSize: bodyFontSize, lineHeight: `${lineSpacing}px`, color: "#555", margin: 0, fontFamily: font, whiteSpace: "pre-line" }}>
              {savedSummary}
            </p>
          </div>
        ) : null;

      case "education":
        const activeEdu = ensureArray(educationData).filter(Boolean);
        return activeEdu.length > 0 ? (
          <div key="education" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>EDUCATION</h2>
            {activeEdu.map((edu, index) => {
              if (!edu || typeof edu !== "object") return null;
              return (
                <div key={edu.id || index} style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: index < activeEdu.length - 1 ? paragraphSpacingLarge : "0" }}>
                  <div style={circleStyle}>○</div>
                  <div style={{ lineHeight: `${lineSpacing}px` }}>
                    <strong style={{ fontSize: strongFontSize, fontFamily: font }}>{edu.schoolName || ""}</strong>
                    {edu.schoolLocation && <span style={{ color: "#666", fontSize: bodyFontSize, fontFamily: font }}> · {edu.schoolLocation}</span>}
                    <br />
                    <span style={{ color: "#666", fontSize: bodyFontSize, fontFamily: font }}>
                      {formatDate(edu.gradMonth, edu.gradYear)}
                      {edu.additionalCoursework ? ` • ${edu.additionalCoursework}` : ""}
                    </span>
                    <br />
                    <span style={{ fontFamily: font, fontSize: bodyFontSize }}>
                      {edu.degree || ""}{edu.fieldOfStudy ? ` - ${edu.fieldOfStudy}` : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null;

      case "workExperience":
        const activeWork = ensureArray(workExperiences).filter(Boolean);
        return activeWork.length > 0 ? (
          <div key="workExperience" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>EXPERIENCE</h2>
            {activeWork.map((work, index) => {
              if (!work || typeof work !== "object") return null;
              return (
                <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: index < activeWork.length - 1 ? paragraphSpacingLarge : "0" }}>
                  <div style={circleStyle}>○</div>
                  <div style={{ flex: 1, lineHeight: `${lineSpacing}px` }}>
                    <strong style={{ fontSize: strongFontSize, fontFamily: font }}>
                      {work.employer || work.company || work.companyName || ""}{work.jobTitle ? ` | ${work.jobTitle}` : ""}
                    </strong>
                    <br />
                    <span style={{ color: "#666", fontSize: bodyFontSize, fontFamily: font }}>
                      {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                    </span>
                    {work.description && (
                      <div
                        style={{ margin: `${itemSpacing} 0 0 0`, fontFamily: font, fontSize: bodyFontSize }}
                        dangerouslySetInnerHTML={{
                          __html: addFontToHtmlContent(
                            work.description
                              .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                              .replace(/\*(.*?)\*/g, "<em>$1</em>")
                              .replace(/<u>(.*?)<\/u>/g, "<u>$1</u>")
                              .replace(/\n/g, "<br/>")
                          ),
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null;

      case "languages":
        const activeRightLangs = ensureArray(savedLanguages).filter(Boolean);
        return activeRightLangs.length > 0 ? (
          <div key="languages" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>LANGUAGES</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontFamily: font }}>
              {activeRightLangs.map((lang, i) => {
                const name = typeof lang === "object" && lang !== null ? (lang.name || lang.language || "") : (lang || "");
                const level = typeof lang === "object" && lang !== null ? (lang.level || "") : "";
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing, fontSize: bodyFontSize, color: "#555", lineHeight: `${lineSpacing}px` }}>
                    <span style={bulletStyle}>•</span>
                    <span>{name}{level ? ` (${level})` : ""}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "accomplishments":
        const activeRightAccs = ensureArray(savedAccomplishments).filter(Boolean);
        return activeRightAccs.length > 0 ? (
          <div key="accomplishments" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>ACCOMPLISHMENTS</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontFamily: font }}>
              {activeRightAccs.map((acc, i) => {
                const text = typeof acc === "object" && acc !== null ? (acc.title || acc.description || acc.name || "") : (acc || "");
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing, fontSize: bodyFontSize, color: "#555", lineHeight: `${lineSpacing}px` }}>
                    <span style={bulletStyle}>•</span>
                    <span>{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "certifications":
        const activeRightCerts = ensureArray(savedCertifications).filter(Boolean);
        return activeRightCerts.length > 0 ? (
          <div key="certifications" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>CERTIFICATIONS</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontFamily: font }}>
              {activeRightCerts.map((cert, i) => {
                const name = typeof cert === "object" && cert !== null ? (cert.name || cert.certName || cert.title || "") : (cert || "");
                const provider = typeof cert === "object" && cert !== null ? (cert.provider || "") : "";
                const year = typeof cert === "object" && cert !== null ? (cert.year || "") : "";
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing, fontSize: bodyFontSize, color: "#555", lineHeight: `${lineSpacing}px` }}>
                    <span style={bulletStyle}>•</span>
                    <span>{name}{provider ? ` – ${provider}` : ""}{year ? ` (${year})` : ""}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "softwareSkills":
        const activeRightSoft = ensureArray(softwareSkills).filter(Boolean);
        return activeRightSoft.length > 0 ? (
          <div key="softwareSkills" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>SOFTWARE SKILLS</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontFamily: font }}>
              {activeRightSoft.map((skill, i) => {
                const name = typeof skill === "object" && skill !== null ? (skill.name || skill.software || "") : (skill || "");
                const level = typeof skill === "object" && skill !== null ? (skill.level || skill.rating || "") : "";
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing, fontSize: bodyFontSize, color: "#555", lineHeight: `${lineSpacing}px` }}>
                    <span style={bulletStyle}>•</span>
                    <span>{name}{level ? ` — ${level}%` : ""}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "volunteering":
        const activeVol = ensureArray(savedVolunteering).filter(Boolean);
        return activeVol.length > 0 ? (
          <div key="volunteering" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>VOLUNTEERING</h2>
            {activeVol.map((vol, index) => {
              if (!vol) return null;
              const role = typeof vol === "object" && vol !== null ? (vol.subtopic || vol.role || vol.title || "") : vol;
              const org = typeof vol === "object" && vol !== null ? (vol.organization || vol.org || "") : "";
              const from = typeof vol === "object" && vol !== null ? (vol.fromDate || vol.startDate || "") : "";
              const to = typeof vol === "object" && vol !== null ? (vol.toDate || vol.endDate || "") : "";
              const desc = typeof vol === "object" && vol !== null ? (vol.content || vol.description || "") : "";
              return (
                <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: index < activeVol.length - 1 ? paragraphSpacingLarge : "0" }}>
                  <div style={circleStyle}>○</div>
                  <div style={{ lineHeight: `${lineSpacing}px` }}>
                    <strong style={{ fontSize: strongFontSize, fontFamily: font }}>{role}{org ? ` — ${org}` : ""}</strong>
                    <br />
                    {(from || to) && (
                      <span style={{ color: "#666", fontSize: bodyFontSize, fontFamily: font }}>
                        {from}{from && to ? " - " : ""}{to}
                      </span>
                    )}
                    {desc && (
                      <p style={{ margin: `${itemSpacing} 0 0 0`, lineHeight: `${lineSpacing}px`, fontFamily: font, fontSize: bodyFontSize, whiteSpace: "pre-line" }}>
                        {desc}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null;

      case "interests":
        const activeRightInts = ensureArray(savedInterests).filter(Boolean);
        return activeRightInts.length > 0 ? (
          <div key="interests" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>INTERESTS</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontFamily: font }}>
              {activeRightInts.map((interest, i) => {
                const text = typeof interest === "object" && interest !== null ? (interest.name || interest.interest || "") : (interest || "");
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing, fontSize: bodyFontSize, color: "#555", lineHeight: `${lineSpacing}px` }}>
                    <span style={bulletStyle}>•</span>
                    <span>{text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "websites":
        const activeRightWeb = ensureArray(savedWebsites).filter(Boolean);
        return activeRightWeb.length > 0 ? (
          <div key="websites" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>WEBSITES / PROFILES</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontFamily: font }}>
              {activeRightWeb.map((site, i) => {
                const url = typeof site === "object" && site !== null ? (site.url || site.link || "") : (site || "");
                const name = typeof site === "object" && site !== null ? (site.name || site.label || url) : url;
                return (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: itemSpacing, fontSize: bodyFontSize, lineHeight: `${lineSpacing}px` }}>
                    <span style={bulletStyle}>•</span>
                    <a
                      href={url && url.startsWith("http") ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1976d2", textDecoration: "none", wordBreak: "break-all" }}
                    >
                      {name || url}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null;

      case "references":
        const activeRightRefs = ensureArray(savedReferences).filter(Boolean);
        return activeRightRefs.length > 0 ? (
          <div key="references" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>REFERENCES</h2>
            {activeRightRefs.map((ref, i) => {
              if (typeof ref === "string") {
                return (
                  <p key={i} style={{ fontSize: bodyFontSize, lineHeight: `${lineSpacing}px`, color: "#555", margin: `0 0 ${itemSpacing} 0`, fontFamily: font, whiteSpace: "pre-line" }}>
                    {ref}
                  </p>
                );
              }
              if (!ref || typeof ref !== "object") return null;
              const name = ref.refName || ref.name || ref.referenceName || "";
              const company = ref.company || ref.companyName || "";
              const titlePos = ref.position || ref.jobTitle || "";
              return (
                <div key={i} style={{ fontSize: bodyFontSize, lineHeight: `${lineSpacing}px`, color: "#555", margin: `0 0 ${itemSpacing} 0`, fontFamily: font }}>
                  <strong>{name}</strong>
                  {(company || titlePos) && <div>{company}{titlePos ? ` / ${titlePos}` : ""}</div>}
                  {ref.phone && <div>Phone: {ref.phone}</div>}
                  {ref.email && <div>Email: {ref.email}</div>}
                </div>
              );
            })}
          </div>
        ) : null;

      case "additionalInfo":
        const activeRightInfo = ensureArray(savedAdditionalInfo).filter(Boolean);
        return activeRightInfo.length > 0 ? (
          <div key="additionalInfo" className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
            <h2 style={rightHeadingStyle}>ADDITIONAL INFORMATION</h2>
            {activeRightInfo.map((info, i) => {
              const text = typeof info === "object" && info !== null ? (info.info || info.text || info.description || "") : (info || "");
              return (
                <p key={i} style={{ fontSize: bodyFontSize, lineHeight: `${lineSpacing}px`, color: "#555", margin: `0 0 ${itemSpacing} 0`, fontFamily: font, whiteSpace: "pre-line" }}>
                  {text}
                </p>
              );
            })}
          </div>
        ) : null;

      default:
        if (sectionId.startsWith("custom_")) {
          const name = sectionId.replace("custom_", "");
          const items = ensureArray(customSections[name]).filter(Boolean);
          return items.length > 0 ? (
            <div key={sectionId} className="resume41-section" style={{ marginBottom: `${sectionSpacingValue * 2}px` }}>
              <h2 style={rightHeadingStyle}>{name.toUpperCase()}</h2>
              {items.map((item, i) => (
                <p key={i} style={{ fontSize: bodyFontSize, lineHeight: `${lineSpacing}px`, color: "#555", margin: `0 0 ${itemSpacing} 0`, fontFamily: font, whiteSpace: "pre-line" }}>
                  • {item}
                </p>
              ))}
            </div>
          ) : null;
        }
        return null;
    }
  };

  // ─── Arrange dialog panel ──────────────────────────────────────────────────
  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === "left" ? "Left Sidebar" : "Main Content"} Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          ({sections.length})
        </Typography>
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? (
          sections.map((sectionId, index) => (
            <Paper
              key={sectionId}
              draggable
              onDragStart={(e) => handleDragStart(e, index, side)}
              onDragOver={(e) => handleDragOver(e, index, side)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index, side)}
              onDragEnd={handleDragEnd}
              sx={{
                p: 2,
                mb: 1,
                display: "flex",
                alignItems: "center",
                backgroundColor: dragOverIndex === index && dragOverSide === side ? "#f0f0f0" : "white",
                boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
                transition: "all 0.2s ease",
              }}
            >
              <DragIndicator color="action" />
              <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
              <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
            </Paper>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: "center" }}>
            No sections. Drag sections from the other side.
          </Typography>
        )}
      </Box>
    </Box>
  );

  // ── Calculate which sections belong to page Index ─────────────────────
  const getSectionsForPage = (pageIdx) => {
    const allSections = rightSectionOrder.filter(isRightSectionActive);
    if (pageBreaks.length === 0 || totalPages === 1) return allSections;

    const container = rightContentRef.current;
    if (!container) return allSections;

    const sections = container.querySelectorAll(".resume41-section");
    const pageHeight = pagePx.height - (topBottomMargin + 20) * 2;

    // Build page chunks by accumulated height
    let accumulated = 0;
    const pageChunks = [[]];
    let currentPIdx = 0;

    sections.forEach((section, i) => {
      const h = section.getBoundingClientRect().height;
      if (accumulated + h > pageHeight * (currentPIdx + 1)) {
        currentPIdx++;
        pageChunks.push([]);
      }
      if (allSections[i]) {
        pageChunks[currentPIdx].push(allSections[i]);
      }
      accumulated += h;
    });

    return pageChunks[pageIdx] || [];
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", position: "relative", m: exporting ? 0 : { xs: 1, md: 5 }, fontFamily: font }}>
      {/* Controls */}
      {!exporting && (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2, justifyContent: "center", "@media print": { display: "none" } }}>
          {/* ── Pagination (same as Resume1) ── */}
          <IconButton
            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            size="small"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {currentPage + 1} / {totalPages}
          </Typography>
          <IconButton
            onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            size="small"
          >
            <ChevronRight />
          </IconButton>

          {enableDragDrop && (
            <>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />} color="primary" size="small">
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset Sections
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden full content for height measurement */}
      <Box
        ref={rightContentRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: "64%",
          top: 0,
          left: "36%",
          zIndex: -1,
        }}
      >
        {rightSectionOrder.map((sectionId) => {
          const content = renderRightSection(sectionId);
          return content ? <React.Fragment key={sectionId}>{content}</React.Fragment> : null;
        })}
      </Box>

      {/* Resume Pages */}
      <Box id="resume-container" sx={{ fontFamily: font }}>
        {Array.from({ length: totalPages }).map((_, pageIndex) => {
          // If not exporting and not the current page, hide it
          if (!exporting && pageIndex !== currentPage) return null;

          return (
            <div
              key={pageIndex}
              className="resume-page"
              style={{
                width: page.width,
                height: page.height,
                margin: "0 auto 24px",
                backgroundColor: "#fdfaf5",
                color: "#2e2e2e",
                fontFamily: font,
                display: "flex",
                boxShadow: exporting ? "none" : "0 4px 20px rgba(0,0,0,0.10)",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              {/* LEFT COLUMN */}
              <div
                style={{
                  width: "36%",
                  minWidth: "36%",
                  backgroundColor: "#f3ede5",
                  padding: `${topBottomMargin + 20}px ${sideMargins - 10}px`,
                  display: "flex",
                  flexDirection: "column",
                  gap: `${sectionSpacingValue * 3}px`,
                  boxSizing: "border-box",
                }}
              >
                {/* Photo — only on page 1 */}
                {pageIndex === 0 && (
                  <div style={{ alignSelf: "center" }}>
                    <img
                      src={
                        photo ||
                        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=987&q=80"
                      }
                      alt={`${data.firstName} ${data.lastName}`}
                      style={{
                        width: "160px",
                        height: "160px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "10px solid #e8d9c5",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                        display: "block",
                      }}
                    />
                  </div>
                )}

                {/* Left sections — only on page 1 */}
                {pageIndex === 0 && leftSectionOrder.map((sectionId) => {
                  const content = renderLeftSection(sectionId);
                  return content ? <React.Fragment key={sectionId}>{content}</React.Fragment> : null;
                })}
              </div>

              {/* RIGHT COLUMN */}
              <div
                style={{
                  width: "64%",
                  padding: `${topBottomMargin + 20}px ${sideMargins + 10}px`,
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  overflowWrap: "break-word",
                  overflow: "hidden",
                }}
              >
                {/* Name & Title — only on page 1 */}
                {pageIndex === 0 && (
                  <div style={{ marginBottom: "20px" }}>
                    <h1
                      style={{
                        fontSize: "2.6rem",
                        fontWeight: "bold",
                        margin: "0 0 6px 0",
                        color: "#3a2f27",
                        lineHeight: "1.1",
                        fontFamily: font,
                      }}
                    >
                      {(data.firstName || "").toUpperCase()}
                      <br />
                      {(data.lastName || "").toUpperCase()}
                    </h1>
                    <h2
                      style={{
                        fontSize: "1.3rem",
                        fontWeight: "600",
                        margin: "0 0 20px 0",
                        color: "#6b5247",
                        fontFamily: font,
                        letterSpacing: "0.5px",
                      }}
                    >
                      {(data.currentPosition || "").toUpperCase()}
                    </h2>
                    <hr style={{ border: "none", borderTop: `${lineWeight}px solid #e0d0bf`, margin: "0 0 24px 0" }} />
                  </div>
                )}

                {/* Right sections for current page */}
                {getSectionsForPage(pageIndex).map((sectionId) => {
                  const content = renderRightSection(sectionId);
                  return content ? <React.Fragment key={sectionId}>{content}</React.Fragment> : null;
                })}
              </div>
            </div>
          );
        })}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag sections to reorder, or move them between sidebar and main content.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1 }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};
export default Resume41;